import { TEntityMilestone, TFeatureWorkflowInstance } from "entity-app/models";
import { useRef } from "react";
import { Button, Tooltip } from "antd";
import DatePickerWithIcon from "components/date-picker-with-icon/date-picker-with-icon";
import { DateUtils } from "utils/dateutils";
import ProgressIndicatorCircle from "components/date-block/progress-indicator-circle";
import { getDaysText } from "utils/utils";
import {
  CalendarOutlined,
  CarryOutOutlined,
  EditOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import { ErrorMessages, EWorkflowStatusDataBlock } from "constants/index";
import { InputEditDataType } from "popups/rename-model/rename-popup";
import { RiskLevelType } from "components/date-block/models";
import CriticalActivityTag from "pages/schedule/schedule-page/critical-activity-tag";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import {
  CIRCLE_WIDTH,
  FLOAT_DEADLINE_POPOVER_WIDTH_PX,
  LEGACY_FLOAT_DEADLINE_POPOVER_LEFT_PX,
  RECT_WIDTH,
  computeFloatDeadlinePopoverLeftPx
} from "../../layout-constants";
import {
  TDateBlockPermissions,
  TGoverningSource,
  TWidthCalculation
} from "../../models";

type TFloatAndDeadlineBlockProps = {
  widthCal: TWidthCalculation;
  featureWorkflowInstance: TFeatureWorkflowInstance;
  applyWorkflowChange: (data: any) => Promise<void>;
  setIsRenameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRenameDataInput: React.Dispatch<React.SetStateAction<InputEditDataType>>;
  governingSource: TGoverningSource | undefined;
  permissions: TDateBlockPermissions;
  entityHeaders: any;
  floatPopoverLeftPx: number;
  allActualSubmitted: boolean;
  isWorkflowStarted: boolean;
  isWorkflowCompleted: boolean;
};

function FloatAndDeadlineBlock({
  widthCal,
  featureWorkflowInstance,
  applyWorkflowChange,
  setIsRenameModalOpen,
  setRenameDataInput,
  governingSource,
  permissions,
  entityHeaders,
  floatPopoverLeftPx,
  allActualSubmitted,
  isWorkflowStarted,
  isWorkflowCompleted
}: Readonly<TFloatAndDeadlineBlockProps>) {
  const childRefPlannedDate = useRef<any>();
  const plannedFloat = featureWorkflowInstance.pre_WF_start_float;
  const actualFloat = featureWorkflowInstance.effective_float;
  const renderExtraFooterDiv = (
    <div className="flex justify-center py-2">
      <Button
        disabled={!permissions.canEditPlannedDate}
        onClick={() => {
          childRefPlannedDate.current?.close();
          applyWorkflowChange({ manual_entry_date: null });
        }}
        data-testid="index-button-clear-date"
      >
        Clear Date
      </Button>
    </div>
  );

  return (
    <div className="flex items-center">
      <div className="bl-line" style={{ width: widthCal.lastLineWidth }} />
      <div className="bl-rect bg-ciq-gray-100">
        <div
          className="absolute top-10 flex flex-col space-y-3 text-xs text-ciq-gray-950"
          style={{
            left: floatPopoverLeftPx,
            width: FLOAT_DEADLINE_POPOVER_WIDTH_PX,
            height: FLOAT_DEADLINE_POPOVER_WIDTH_PX
          }}
          data-testid="timeline-iconsonly-float-popover"
        >
          <div className="w-full bg-ciq-neutral-50 p-1">
            <div className="flex justify-between p-1 items-center">
              <div>Planned Float</div>
              <div className="font-semibold space-x-1 flex items-center">
                <div>
                  {plannedFloat} {getDaysText(plannedFloat)}
                </div>
                {permissions.canEditDurations && !isWorkflowStarted && (
                  <EditOutlined
                    className="pl-1"
                    onClick={() => {
                      setRenameDataInput({
                        value: plannedFloat,
                        id: featureWorkflowInstance.id,
                        lable: "Float",
                        type: "text",
                        identityName: "WorkflowInstanceFloat"
                      });
                      setIsRenameModalOpen(true);
                    }}
                  />
                )}
              </div>
            </div>
            <div className="flex justify-between p-1 items-center">
              <div>Actual Float</div>
              <div
                className={
                  featureWorkflowInstance.risk_level === RiskLevelType.High
                    ? "date-block-risk-high font-semibold"
                    : "font-semibold"
                }
              >
                {actualFloat != null
                  ? `${actualFloat} ${getDaysText(actualFloat)}`
                  : " - "}
              </div>
            </div>
          </div>
          <div className="w-full p-1 bg-ciq-neutral-50">
            <div className="flex justify-between p-1 space-x-1 items-baseline">
              <TruncatedTextWithTooltip
                text={entityHeaders?.final_deadline_date ?? "Deadline"}
                className="min-w-0 flex-1"
                dataTestId="timeline-iconsonly-deadline-label"
              />
              <div className="font-semibold flex shrink-0 space-x-1 items-center">
                {featureWorkflowInstance.final_deadline_date ? (
                  <div className="w-20">
                    {DateUtils.format(
                      featureWorkflowInstance.final_deadline_date
                    )}
                  </div>
                ) : (
                  <div className="px-1">-</div>
                )}
                {featureWorkflowInstance.final_deadline_date && (
                  <Tooltip
                    title={
                      governingSource ? (
                        <div className="p-2">
                          <div className="text-ciq-gray-700">Source:</div>
                          <div>{governingSource.type}</div>
                          {governingSource.name && (
                            <div className="pb-2">({governingSource.name})</div>
                          )}
                          {governingSource.type === "Linked Activity" && (
                            <CriticalActivityTag />
                          )}
                        </div>
                      ) : (
                        ""
                      )
                    }
                  >
                    <InfoCircleOutlined className="mb-px" />
                  </Tooltip>
                )}

                <div className="mb-1 flex">
                  <DatePickerWithIcon
                    title={
                      !permissions.canEditPlannedDate
                        ? ErrorMessages.PermissionNotGranted
                        : "Set date to be no later than"
                    }
                    disabledDate={() => {
                      return (
                        !permissions.canEditPlannedDate ||
                        allActualSubmitted ||
                        isWorkflowCompleted
                      );
                    }}
                    value={
                      featureWorkflowInstance.manual_entry_date
                        ? DateUtils.dateTimeObj(
                            featureWorkflowInstance.manual_entry_date
                          )
                        : undefined
                    }
                    icon={
                      featureWorkflowInstance.manual_entry_date ? (
                        <CarryOutOutlined />
                      ) : (
                        <CalendarOutlined />
                      )
                    }
                    onChange={(date) => {
                      if (date) {
                        const saveDate =
                          DateUtils.formatDateWithLunchTime(date);
                        const set = { manual_entry_date: saveDate };
                        applyWorkflowChange(set);
                      }
                    }}
                    ref={childRefPlannedDate}
                    renderExtraFooter={() => {
                      return featureWorkflowInstance.manual_entry_date
                        ? renderExtraFooterDiv
                        : undefined;
                    }}
                  />
                  {governingSource?.name === "Manually Set Date" && (
                    <div className="-ml-0.5">*</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Trailing segment after a milestone: the offset rect when a real offset
 * exists. On a Material Focused View, the last milestone with no offset (e.g.
 * On-site Storage missing) instead renders a plain line of the same footprint
 * so the Float & Deadline block stays put and dimensions are unchanged.
 */
function TrailingSegment({
  milestone,
  isLast,
  lineWidth,
  materialFocusedLayout
}: Readonly<{
  milestone: TEntityMilestone;
  isLast: boolean;
  lineWidth: number;
  materialFocusedLayout: boolean;
}>) {
  const hasOffset =
    (milestone.feature_workflow_instance_offsets_as_src?.length ?? 0) > 0;
  if (hasOffset) {
    return (
      <div
        className="flex items-center"
        data-testid={`timeline-iconsonly-offset-${milestone.id}`}
      >
        <div
          className="bl-line"
          style={{ width: lineWidth }}
          data-testid={`timeline-iconsonly-offset-line-${milestone.id}`}
        />
        <div
          className="bl-rect"
          data-testid={`timeline-iconsonly-offset-rect-${milestone.id}`}
        />
      </div>
    );
  }
  if (isLast && materialFocusedLayout) {
    return (
      <div
        className="flex items-center"
        data-testid={`timeline-iconsonly-offset-line-fill-${milestone.id}`}
      >
        <div className="bl-line" style={{ width: lineWidth + RECT_WIDTH }} />
      </div>
    );
  }
  return null;
}

export default function TimelineIconsOnly(props: {
  milestones: Array<TEntityMilestone>;
  currentMilestone: TEntityMilestone | undefined;
  widthCal: TWidthCalculation;
  featureWorkflowInstance: TFeatureWorkflowInstance;
  applyWorkflowChange: (data: any) => Promise<void>;
  showFloat: boolean;
  setIsRenameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRenameDataInput: React.Dispatch<React.SetStateAction<InputEditDataType>>;
  governingSource: TGoverningSource | undefined;
  permissions: TDateBlockPermissions;
  entityHeaders: any;
  materialFocusedLayout?: boolean;
  designPackageFocusedLayout?: boolean;
}) {
  const {
    milestones,
    currentMilestone,
    widthCal,
    featureWorkflowInstance,
    applyWorkflowChange,
    showFloat,
    governingSource,
    setIsRenameModalOpen,
    setRenameDataInput,
    permissions,
    entityHeaders,
    materialFocusedLayout = false,
    designPackageFocusedLayout = false
  } = props;
  const { length } = milestones;

  const lineWidth = (widthCal.timelineWidth - CIRCLE_WIDTH - RECT_WIDTH) / 2;
  const leftMargin = (widthCal.milestonesWidth - CIRCLE_WIDTH) / 2;

  const allActualSubmitted = milestones.every((m) => m.actual_date);

  const isWorkflowStarted =
    (featureWorkflowInstance.workflow_status as string) !==
    (EWorkflowStatusDataBlock.WF_NOT_STARTED as string);

  const isWorkflowCompleted =
    (featureWorkflowInstance?.workflow_status as string) ===
    (EWorkflowStatusDataBlock.WF_COMPLETED as string);

  const floatPopoverLeftPx = materialFocusedLayout
    ? computeFloatDeadlinePopoverLeftPx(milestones.length)
    : LEGACY_FLOAT_DEADLINE_POPOVER_LEFT_PX;

  return (
    <div className="flex h-10" data-testid="timeline-iconsonly-root">
      <div className="shrink-0" style={{ width: leftMargin }} />
      {milestones.map((m, index) => {
        const isNextMileStone = length !== index + 1;

        return (
          <div
            key={`TimelineIconsOnly_${m.id}`}
            className="flex relative items-center"
            data-testid={`timeline-iconsonly-milestone-${m.id}`}
          >
            <ProgressIndicatorCircle
              actualDate={m.actual_date}
              isCurrentMilestone={m === currentMilestone}
              maskCenterConnector={
                designPackageFocusedLayout && length - 1 === index
              }
              data-testid={`timeline-iconsonly-progress-indicator-${m.id}`}
            />
            {length - 1 === index && showFloat && (
              <div data-testid="timeline-iconsonly-float-block">
                <FloatAndDeadlineBlock
                  widthCal={widthCal}
                  featureWorkflowInstance={featureWorkflowInstance}
                  applyWorkflowChange={applyWorkflowChange}
                  setIsRenameModalOpen={setIsRenameModalOpen}
                  setRenameDataInput={setRenameDataInput}
                  governingSource={governingSource}
                  permissions={permissions}
                  entityHeaders={entityHeaders}
                  floatPopoverLeftPx={floatPopoverLeftPx}
                  allActualSubmitted={allActualSubmitted}
                  isWorkflowStarted={isWorkflowStarted}
                  isWorkflowCompleted={isWorkflowCompleted}
                />
              </div>
            )}

            <TrailingSegment
              milestone={m}
              isLast={length - 1 === index}
              lineWidth={lineWidth}
              materialFocusedLayout={materialFocusedLayout}
            />
            {isNextMileStone && (
              <div
                className="bl-line"
                style={{ width: lineWidth }}
                data-testid={`timeline-iconsonly-next-line-${m.id}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
